import React from 'react';
import { usePaginationFragment } from 'react-relay/hooks';
import { graphql } from 'react-relay';
import classes from './TransactionList.module.scss';
import TransactionItem from '../TransactionItem/TransactionItem';
import { TransactionList_transactions$key } from './__generated__/TransactionList_transactions.graphql';
import Button from '../../atoms/Button/Button';

import Txt from '../../atoms/Txt/Txt';

export interface Props {
  transactionsRef: TransactionList_transactions$key;
}

/*
  This fragment is designed this way to meet the `usePaginationFragment` requirments.
  The $count and $cursor argument values are provided by the usePaginationFragment feature.
  We declare the custom required inputs in the parent query snippet, as this fragment
  is included as a spread, so the references just implicitly resolve/work.
  The transaction query function parameters relate to the pagination functionality.
  'First' refers to the number of elements that you want to acquire per pagination request.
  'After' is the internal cursor which is used to keep track of which current page/offset
  the pagination is on. This is a discrete string which is managed by the API gateway.
  The input object is how we are able to pass in query arguments to select the data on.
*/

const paginationFragment = graphql`
  fragment TransactionList_transactions on Query
  @argumentDefinitions(count: { type: "Int", defaultValue: 20 }, cursor: { type: "String" })
  @refetchable(queryName: "TransactionListPaginationQuery") {
    transactions(first: $count, after: $cursor)
      @connection(key: "TransactionList_query_transactions") {
      edges {
        node {
          id
          ...TransactionItem_transaction
        }
      }
      pageInfo {
        hasNextPage
      }
    }
  }
`;

const TransactionList: React.FC<Props> = ({ transactionsRef }) => {
  const { data, loadNext, isLoadingNext } = usePaginationFragment(
    paginationFragment,
    transactionsRef,
  );

  return (
    <section className={classes.transactionListContainer}>
      <Txt component="h2">Transaction History</Txt>
      <table className={classes.table}>
        <tbody>
          {data.transactions?.edges.map((edge) => (
            <TransactionItem key={edge.node.id} transactionRef={edge.node} />
          ))}
        </tbody>
      </table>
      {data.transactions?.pageInfo?.hasNextPage ? (
        <Button
          disabled={isLoadingNext}
          className={classes.button}
          buttonType="tertiary"
          onClick={() => {
            loadNext(20);
          }}
        >
          <Txt component="p">Show More</Txt>
        </Button>
      ) : null}
    </section>
  );
};

export default TransactionList;
